import React from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import SelectionController from './SelectionController';
import type RecordAction from './models/RecordAction';

export type MenuItem<T extends {} = {}> = {
  label: string;
  data: T;
  icon?: string;
};

export type GroupedMenu<T extends {} = {}> = Record<string, MenuItem<T>[]>;
export type MenuContent<T extends {} = {}> = MenuItem<T>[] | GroupedMenu<T>;

interface ActionMenuItemProps {
  action: RecordAction;
  item?: MenuItem;
}

export function getIcon(action: RecordAction, item?: MenuItem) {
  const icon = item ? item.icon : action.icon;
  return icon ? <i className={icon} /> : null;
}

const ActionTriggerMenuItem: React.FC<ActionMenuItemProps> = ({
  action,
  item,
}) => {
  const controller = useController(SelectionController);

  return (
    <aha-menu-item
      onClick={() => controller.actionTriggerRecordAction(action, item?.data)}
      type={action.menuItemType}
    >
      <div className='aha-menu-item__link'>
        {getIcon(action, item)}
        {item?.label || action.title}
      </div>
    </aha-menu-item>
  );
};

const ActionRemoteMenuItem: React.FC<ActionMenuItemProps> = ({
  action,
  item,
}) => {
  const controller = useController(SelectionController);
  const { selectedItems } = controller;
  const attributes = {
    ...action.remoteLinkAttributes(selectedItems),
    ...item?.data,
  };

  return (
    <aha-menu-item>
      <a
        href={attributes.href}
        data-method={attributes.method || 'get'}
        data-remote
      >
        {getIcon(action, item)}
        {item?.label || action.title}
      </a>
    </aha-menu-item>
  );
};

/**
 * Create a menu item for an action. The `item` prop is optional and used when
 * creating multiple submenu items for a single action
 */
const ActionMenuItem: React.FC<ActionMenuItemProps> = ({ action, item }) => {
  if (action.isRemoteLink) {
    return <ActionRemoteMenuItem action={action} item={item} />;
  } else {
    return <ActionTriggerMenuItem action={action} item={item} />;
  }
};

export default ApplicationView(ActionMenuItem);
